/**
 * F-BULK-IMPORT (2026-05-24) — bulk-mode wizard.
 *
 * Sibling to ImportWizard. The single-pick wizard runs in-browser
 * (loops `upsertFromExternal` on a user-selected file set); this one
 * hands a whole drive/folder/site/search-query off to the Azure
 * Container Apps Job `repancy-bulk-import-job` for an async drain.
 *
 * Steps:
 *   1. scope     — pick site/drive/folder (search is v2)
 *   2. mapping   — single-matter vs folder-per-matter
 *   3. options   — link vs copy, dry-run
 *   4. review    — sample-20 preview + matter target
 *   5. kick      — capture delegated Graph token, mutation
 *                  `bulkImport.create`, action `bulkImport.kickWorker`
 *
 * On successful kick, fires `onLaunched(jobId)` so the host can
 * open the ImportJobProgressWindow. The wizard then closes itself.
 *
 * Design source: future-plans/real-sharepoint/bulk-worker-aca-job.md.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Drive, DriveItem, Site } from '@microsoft/microsoft-graph-types';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { listChildren, listDrives, listSites } from '../../lib/graph/sharepoint';
import { acquireGraphToken } from '../../lib/graph/msalClient';
import { isFile } from '@repancy/graph-core';
import { HelpTip } from '../ui/HelpTip';
import { testIds } from '../../lib/testIds';

const FONT = "var(--xp-font-body)";

type BulkStep = 'scope' | 'mapping' | 'options' | 'review' | 'kicking';
type ScopeKind = 'site' | 'drive' | 'folder';
type MappingPolicy = 'single-matter' | 'folder-per-matter';
type LinkMode = 'link' | 'copy';

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
};

const dialogStyle: React.CSSProperties = {
  width: 680,
  height: 540,
  background: '#ece9d8',
  borderStyle: 'solid',
  borderWidth: 2,
  boxShadow: '3px 3px 0 #000',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: FONT,
  fontSize: 12,
};

const titleBarStyle: React.CSSProperties = {
  background: 'linear-gradient(90deg, var(--xp-selection), #1a4ab8)',
  color: 'white',
  padding: '3px 6px',
  fontWeight: 'bold',
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  padding: 14,
  overflow: 'auto',
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 6,
  padding: '8px 12px',
  borderTop: '1px solid #808080',
};

const STEP_TITLES: Record<BulkStep, string> = {
  scope: 'Step 1 of 4: Pick what to import',
  mapping: 'Step 2 of 4: Map files to matters',
  options: 'Step 3 of 4: Options',
  review: 'Step 4 of 4: Review and start',
  kicking: 'Starting the importer…',
};

function Btn({
  label,
  onClick,
  disabled,
  primary,
  testid,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
  testid?: string;
}) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testid}
      style={{
        minWidth: 90,
        height: 26,
        padding: '0 12px',
        background: '#ece9d8',
        borderStyle: 'solid',
        borderWidth: 2,
        fontFamily: FONT,
        fontSize: 12,
        fontWeight: primary ? 'bold' : 'normal',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? '#808080' : '#000',
      }}
    >
      {label}
    </button>
  );
}

export interface BulkImportWizardProps {
  open: boolean;
  onCancel: () => void;
  /** Called after `bulkImport.create + kickWorker` returns ok. */
  onLaunched?: (jobId: Id<'importJobs'>) => void;
  /** The current matter context — pre-fills the matter picker. */
  defaultMatterId?: Id<'matters'>;
}

interface MatterRow {
  _id: Id<'matters'>;
  matterCode?: string;
  name?: string;
}

export function BulkImportWizard({
  open,
  onCancel,
  onLaunched,
  defaultMatterId,
}: BulkImportWizardProps) {
  // ─── Step + selection state ──────────────────────────────────────
  const [step, setStep] = useState<BulkStep>('scope');
  const [scopeKind, setScopeKind] = useState<ScopeKind>('drive');
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [selectedDrive, setSelectedDrive] = useState<Drive | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<DriveItem | null>(null);
  const [mappingPolicy, setMappingPolicy] = useState<MappingPolicy>('single-matter');
  const [linkMode, setLinkMode] = useState<LinkMode>('link');
  const [dryRun, setDryRun] = useState(false);
  const [matterId, setMatterId] = useState<Id<'matters'> | null>(
    defaultMatterId ?? null,
  );

  // ─── Picker data ─────────────────────────────────────────────────
  const [sites, setSites] = useState<Site[]>([]);
  const [drives, setDrives] = useState<Drive[]>([]);
  const [children, setChildren] = useState<DriveItem[]>([]);
  const [sample, setSample] = useState<DriveItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ─── Convex wiring ───────────────────────────────────────────────
  const createBulkJob = useMutation(api.bulkImport.create);
  const kickWorker = useAction(api.bulkImport.kickWorker);
  const matters = useQuery(api.matters.getMattersByClient, 'skip') as
    | MatterRow[]
    | undefined;
  // The matter picker uses the current matter context (defaultMatterId)
  // rather than a full client picker — v1 covers the in-place "import
  // a drive into the matter I'm looking at" flow.

  // Reset on open.
  useEffect(() => {
    if (open) {
      setStep('scope');
      setScopeKind('drive');
      setSelectedSite(null);
      setSelectedDrive(null);
      setSelectedFolder(null);
      setMappingPolicy('single-matter');
      setLinkMode('link');
      setDryRun(false);
      setMatterId(defaultMatterId ?? null);
      setSites([]);
      setDrives([]);
      setChildren([]);
      setSample([]);
      setError(null);
    }
  }, [open, defaultMatterId]);

  // Fetch sites when entering scope step.
  useEffect(() => {
    if (!open || step !== 'scope') return;
    setLoading(true);
    setError(null);
    listSites()
      .then(setSites)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, [open, step]);

  // Fetch drives when a site is selected.
  useEffect(() => {
    if (step !== 'scope' || !selectedSite?.id) return;
    listDrives(selectedSite.id)
      .then(setDrives)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [step, selectedSite]);

  // Fetch folder children when a drive is selected (only if scopeKind=folder).
  useEffect(() => {
    if (step !== 'scope' || scopeKind !== 'folder' || !selectedDrive?.id) return;
    listChildren(selectedDrive.id, 'root')
      .then(setChildren)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [step, scopeKind, selectedDrive]);

  // When entering review step, fetch a sample of 20 files for preview.
  useEffect(() => {
    if (step !== 'review' || !selectedDrive?.id) return;
    const root = selectedFolder?.id ?? 'root';
    setLoading(true);
    listChildren(selectedDrive.id, root)
      .then((items) =>
        setSample(items.filter(isFile).slice(0, 20)),
      )
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, [step, selectedDrive, selectedFolder]);

  const canAdvanceScope = useMemo(() => {
    if (scopeKind === 'site') return !!selectedSite?.id;
    if (scopeKind === 'drive') return !!(selectedSite?.id && selectedDrive?.id);
    if (scopeKind === 'folder')
      return !!(selectedSite?.id && selectedDrive?.id && selectedFolder?.id);
    return false;
  }, [scopeKind, selectedSite, selectedDrive, selectedFolder]);

  const canKick = useMemo(
    () => step === 'review' && matterId !== null,
    [step, matterId],
  );

  if (!open) return null;

  async function handleKick(): Promise<void> {
    if (!matterId) {
      setError('Pick a target matter before starting the import.');
      return;
    }
    setStep('kicking');
    setError(null);
    try {
      // Capture delegated Graph token at "start" click (explicit user
      // gesture per acquireGraphToken popup-policy comment).
      const graphAccessToken = await acquireGraphToken();
      // Graph tokens are typically 60min; record a 50min ceiling so
      // the UI can show a conservative "expires in" badge. The worker
      // doesn't trust this — Graph 401 is the real signal.
      const expiresAt = new Date(Date.now() + 50 * 60 * 1000).toISOString();

      const sourceRef: Record<string, string | undefined> = {};
      if (selectedSite?.id) sourceRef.siteId = selectedSite.id;
      if (selectedDrive?.id) sourceRef.driveId = selectedDrive.id;
      if (selectedFolder?.id) sourceRef.folderId = selectedFolder.id;

      const jobId = await createBulkJob({
        matterId,
        sourceKind: scopeKind,
        sourceRef,
        mappingPolicy,
        linkMode,
        dryRun,
        graphAccessToken,
        graphAccessTokenExpiresAt: expiresAt,
      });

      const result = await kickWorker({ jobId });
      if (!result.ok) {
        throw new Error(result.reason ?? 'kickWorker failed');
      }
      onLaunched?.(jobId);
      onCancel(); // close wizard; host shows progress window
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStep('review');
    }
  }

  return (
    <div
      style={overlayStyle}
      data-testid={testIds.bulkImport.wizardOverlay}
      onClick={onCancel}
    >
      <div
        style={dialogStyle}
        data-testid={testIds.bulkImport.wizard}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={titleBarStyle} data-testid={testIds.bulkImport.stepHeader}>
          Bulk SharePoint import — {STEP_TITLES[step]}
        </div>
        <div style={bodyStyle}>
          {error && (
            <div style={{ color: '#800', marginBottom: 8 }}>{error}</div>
          )}
          {loading && <div style={{ color: '#444' }}>Loading…</div>}

          {step === 'scope' && (
            <div data-testid={testIds.bulkImport.scopeStep}>
              <div style={{ marginBottom: 10 }}>
                <strong>What do you want to import?</strong>
                <div style={{ marginTop: 4 }}>
                  {(['site', 'drive', 'folder'] as ScopeKind[]).map((k) => (
                    <label key={k} style={{ marginRight: 14 }}>
                      <input
                        type="radio"
                        name="scope-kind"
                        value={k}
                        checked={scopeKind === k}
                        onChange={() => setScopeKind(k)}
                        data-testid={testIds.bulkImport.scopeKindRadio(k)}
                      />{' '}
                      <span style={{ textTransform: 'capitalize' }}>{k}</span>
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: '#444', marginTop: 4 }}>
                  {scopeKind === 'site' &&
                    "Imports every file from the site's default document library."}
                  {scopeKind === 'drive' &&
                    'Imports every file from a specific library.'}
                  {scopeKind === 'folder' &&
                    'Imports every file inside one folder (and its subfolders).'}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <SiteList
                  sites={sites}
                  selectedId={selectedSite?.id ?? null}
                  onSelect={(s) => {
                    setSelectedSite(s);
                    setSelectedDrive(null);
                    setSelectedFolder(null);
                  }}
                />
                {scopeKind !== 'site' && (
                  <DriveList
                    drives={drives}
                    selectedId={selectedDrive?.id ?? null}
                    onSelect={(d) => {
                      setSelectedDrive(d);
                      setSelectedFolder(null);
                    }}
                    placeholder={selectedSite ? 'Loading libraries…' : 'Pick a site first.'}
                  />
                )}
              </div>

              {scopeKind === 'folder' && selectedDrive && (
                <div style={{ marginTop: 10 }}>
                  <strong>Pick a folder</strong>
                  <FolderList
                    folders={children.filter((c) => c.folder)}
                    selectedId={selectedFolder?.id ?? null}
                    onSelect={setSelectedFolder}
                  />
                </div>
              )}
            </div>
          )}

          {step === 'mapping' && (
            <div data-testid={testIds.bulkImport.mappingStep}>
              <strong>How should files be mapped to matters?</strong>
              <div style={{ marginTop: 8 }}>
                <HelpTip k="bulkImport.mapping.singleMatter" wrap>
                  <label style={{ display: 'block', marginBottom: 8 }}>
                    <input
                      type="radio"
                      name="mapping-policy"
                      value="single-matter"
                      checked={mappingPolicy === 'single-matter'}
                      onChange={() => setMappingPolicy('single-matter')}
                      data-testid={testIds.bulkImport.mappingPolicyRadio(
                        'single-matter',
                      )}
                    />{' '}
                    All files land in the same matter (pick the target on the next
                    step).
                  </label>
                </HelpTip>
                <HelpTip k="bulkImport.mapping.folderPerMatter" wrap>
                  <label style={{ display: 'block', marginBottom: 8, opacity: 0.5 }}>
                    <input
                      type="radio"
                      name="mapping-policy"
                      value="folder-per-matter"
                      checked={mappingPolicy === 'folder-per-matter'}
                      onChange={() => setMappingPolicy('folder-per-matter')}
                      disabled
                      data-testid={testIds.bulkImport.mappingPolicyRadio(
                        'folder-per-matter',
                      )}
                    />{' '}
                    Each top-level folder becomes its own matter.{' '}
                    <em>(v2 — coming soon)</em>
                  </label>
                </HelpTip>
              </div>
            </div>
          )}

          {step === 'options' && (
            <div data-testid={testIds.bulkImport.optionsStep}>
              <div style={{ marginBottom: 12 }}>
                <strong>Link mode</strong>
                <div style={{ marginTop: 4 }}>
                  <label style={{ marginRight: 14 }}>
                    <input
                      type="radio"
                      name="link-mode"
                      value="link"
                      checked={linkMode === 'link'}
                      onChange={() => setLinkMode('link')}
                      data-testid={testIds.bulkImport.linkModeRadio('link')}
                    />{' '}
                    Link (files stay in SharePoint, Repancy keeps a reference)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="link-mode"
                      value="copy"
                      checked={linkMode === 'copy'}
                      onChange={() => setLinkMode('copy')}
                      data-testid={testIds.bulkImport.linkModeRadio('copy')}
                    />{' '}
                    Copy (one-shot snapshot into Repancy)
                  </label>
                </div>
              </div>

              <div>
                <label>
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    data-testid={testIds.bulkImport.dryRunCheckbox}
                  />{' '}
                  <strong>Dry run</strong> — preview only; no files imported
                </label>
              </div>
            </div>
          )}

          {step === 'review' && (
            <div data-testid={testIds.bulkImport.reviewStep}>
              <div style={{ marginBottom: 10 }}>
                <strong>Target matter</strong>
                <div style={{ marginTop: 4 }}>
                  <select
                    value={matterId ?? ''}
                    onChange={(e) =>
                      setMatterId(
                        e.target.value
                          ? (e.target.value as Id<'matters'>)
                          : null,
                      )
                    }
                    data-testid={testIds.bulkImport.matterPicker}
                    style={{ width: '100%', padding: 4 }}
                  >
                    <option value="">— pick a matter —</option>
                    {(matters ?? []).map((m) => (
                      <option
                        key={m._id}
                        value={m._id}
                        data-testid={testIds.bulkImport.matterOption(m._id)}
                      >
                        {m.matterCode ? `${m.matterCode} — ` : ''}
                        {m.name ?? m._id}
                      </option>
                    ))}
                    {defaultMatterId && !matters?.some((m) => m._id === defaultMatterId) && (
                      <option value={defaultMatterId}>
                        (current matter)
                      </option>
                    )}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 10 }}>
                <strong>Summary</strong>
                <ul style={{ margin: '4px 0 0 18px' }}>
                  <li>Scope: {scopeKind} ({selectedDrive?.name ?? selectedSite?.displayName ?? '—'})</li>
                  <li>Mapping: {mappingPolicy}</li>
                  <li>Link mode: {linkMode}</li>
                  <li>Dry run: {dryRun ? 'yes' : 'no'}</li>
                </ul>
              </div>

              <div data-testid={testIds.bulkImport.samplePreview}>
                <strong>Sample of files we'd import</strong>{' '}
                <span style={{ color: '#666' }}>
                  (showing first {sample.length}; total may be larger)
                </span>
                <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0' }}>
                  {sample.map((item, idx) => (
                    <li
                      key={item.id ?? idx}
                      data-testid={testIds.bulkImport.sampleRow(idx)}
                      style={{ padding: '2px 0' }}
                    >
                      📄 {item.name}
                      {typeof item.size === 'number' && (
                        <span style={{ color: '#666' }}>
                          {' '}
                          — {Math.round(item.size / 1024)} KB
                        </span>
                      )}
                    </li>
                  ))}
                  {sample.length === 0 && !loading && (
                    <li style={{ color: '#666' }}>
                      No files visible at the scope root. Subfolders may still
                      contain files; the worker walks the full tree.
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}

          {step === 'kicking' && (
            <div>
              Capturing your Microsoft sign-in and starting the worker…
            </div>
          )}
        </div>

        <div style={footerStyle}>
          <Btn
            label="Cancel"
            onClick={onCancel}
            testid={testIds.bulkImport.cancelButton}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            {step !== 'scope' && step !== 'kicking' && (
              <Btn
                label="◂ Back"
                onClick={() => {
                  if (step === 'mapping') setStep('scope');
                  if (step === 'options') setStep('mapping');
                  if (step === 'review') setStep('options');
                }}
                testid={testIds.bulkImport.backButton}
              />
            )}
            {step !== 'review' && step !== 'kicking' && (
              <Btn
                label="Next ▸"
                onClick={() => {
                  if (step === 'scope') setStep('mapping');
                  else if (step === 'mapping') setStep('options');
                  else if (step === 'options') setStep('review');
                }}
                disabled={step === 'scope' && !canAdvanceScope}
                testid={testIds.bulkImport.nextButton}
                primary
              />
            )}
            {step === 'review' && (
              <Btn
                label={dryRun ? 'Start dry run' : 'Start import'}
                onClick={() => void handleKick()}
                disabled={!canKick}
                testid={testIds.bulkImport.kickButton}
                primary
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-pickers ──────────────────────────────────────────────────────

function SiteList({
  sites,
  selectedId,
  onSelect,
}: {
  sites: Site[];
  selectedId: string | null;
  onSelect: (s: Site) => void;
}) {
  return (
    <div>
      <strong>Sites</strong>
      <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0 0', maxHeight: 220, overflow: 'auto' }}>
        {sites.map((s) => (
          <li
            key={s.id}
            onClick={() => onSelect(s)}
            data-testid={s.id ? testIds.bulkImport.siteRow(s.id) : undefined}
            style={{
              padding: '4px 6px',
              cursor: 'pointer',
              background: selectedId === s.id ? 'var(--xp-selection)' : 'transparent',
              color: selectedId === s.id ? 'white' : '#000',
            }}
          >
            📁 {s.displayName ?? s.name}
          </li>
        ))}
        {sites.length === 0 && (
          <li style={{ color: '#666', padding: '4px 6px' }}>(no sites)</li>
        )}
      </ul>
    </div>
  );
}

function DriveList({
  drives,
  selectedId,
  onSelect,
  placeholder,
}: {
  drives: Drive[];
  selectedId: string | null;
  onSelect: (d: Drive) => void;
  placeholder: string;
}) {
  return (
    <div>
      <strong>Libraries</strong>
      <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0 0', maxHeight: 220, overflow: 'auto' }}>
        {drives.map((d) => (
          <li
            key={d.id}
            onClick={() => onSelect(d)}
            data-testid={d.id ? testIds.bulkImport.driveRow(d.id) : undefined}
            style={{
              padding: '4px 6px',
              cursor: 'pointer',
              background: selectedId === d.id ? 'var(--xp-selection)' : 'transparent',
              color: selectedId === d.id ? 'white' : '#000',
            }}
          >
            📚 {d.name}
          </li>
        ))}
        {drives.length === 0 && (
          <li style={{ color: '#666', padding: '4px 6px' }}>{placeholder}</li>
        )}
      </ul>
    </div>
  );
}

function FolderList({
  folders,
  selectedId,
  onSelect,
}: {
  folders: DriveItem[];
  selectedId: string | null;
  onSelect: (f: DriveItem) => void;
}) {
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0 0', maxHeight: 160, overflow: 'auto' }}>
      {folders.map((f) => (
        <li
          key={f.id}
          onClick={() => onSelect(f)}
          data-testid={f.id ? testIds.bulkImport.folderRow(f.id) : undefined}
          style={{
            padding: '4px 6px',
            cursor: 'pointer',
            background: selectedId === f.id ? 'var(--xp-selection)' : 'transparent',
            color: selectedId === f.id ? 'white' : '#000',
          }}
        >
          📁 {f.name}{' '}
          {typeof f.folder?.childCount === 'number' && (
            <span style={{ color: '#666' }}>({f.folder.childCount})</span>
          )}
        </li>
      ))}
      {folders.length === 0 && (
        <li style={{ color: '#666', padding: '4px 6px' }}>(no folders)</li>
      )}
    </ul>
  );
}
